// src/components/ads/AdBanner.tsx
import React, { useEffect, useRef } from 'react';
import { ADSENSE_CLIENT, AD_SLOTS, AdSlotKey } from '../../config/ads';

interface AdBannerProps {
    /** config/ads.ts 의 AD_SLOTS 키 */
    slot: AdSlotKey;
    /** 'auto' | 'rectangle' | 'horizontal' | 'fluid' 등 */
    format?: string;
    /** 사이드바처럼 폭이 고정된 곳에서는 false */
    responsive?: boolean;
    className?: string;
    label?: string;
}

declare global {
    interface Window {
        adsbygoogle: unknown[];
    }
}

/**
 * 수동 애드센스 광고 단위.
 *
 * config/ads.ts 의 슬롯 ID가 비어 있으면 아무것도 렌더링하지 않습니다.
 * (자동광고는 index.html 스크립트가 계속 처리하므로 영향 없음)
 */
const AdBanner: React.FC<AdBannerProps> = ({
    slot,
    format = 'auto',
    responsive = true,
    className = '',
    label = '광고',
}) => {
    const slotId = AD_SLOTS[slot];
    const insRef = useRef<HTMLModElement>(null);
    const pushed = useRef(false);

    useEffect(() => {
        if (!slotId || pushed.current) return;
        // 프리렌더된 <ins> 를 자동광고 스크립트가 이미 채웠다면 다시 push 하지 않음
        if (insRef.current?.dataset.adsbygoogleStatus) return;
        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            pushed.current = true;
        } catch {
            /* 광고 차단기 등으로 실패해도 페이지 동작에는 영향 없음 */
        }
    }, [slotId]);

    // 슬롯 미설정 — 개발 중에는 자리만 표시하고, 실제 서비스에서는 렌더링하지 않음
    if (!slotId) {
        if (process.env.NODE_ENV === 'development') {
            return (
                <div className={`gh-ad ${className}`}>
                    <div className="gh-ad__ph">
                        애드센스 수동 광고 자리 · {slot}
                        <br />
                        src/config/ads.ts 에 슬롯 ID를 입력하면 노출됩니다
                    </div>
                </div>
            );
        }
        return null;
    }

    return (
        <div className={`gh-ad ${className}`}>
            <span className="gh-ad__label">{label}</span>
            <ins
                ref={insRef}
                className="adsbygoogle gh-ad__slot"
                style={{ display: 'block' }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={slotId}
                data-ad-format={format}
                data-full-width-responsive={responsive ? 'true' : 'false'}
            />
        </div>
    );
};

export default AdBanner;
